import { Alert, Card } from "@aman/design-system";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { api, asApiError, type ApiError } from "../api/client";
import { PageHeader } from "../components/common";
import { objectFromPayload, stringValue } from "../types";

interface PluginRequestMessage {
  type?: unknown;
  requestId?: unknown;
  path?: unknown;
  method?: unknown;
  body?: unknown;
  height?: unknown;
}

const allowedMethods = new Set(["GET", "POST", "PUT", "PATCH", "DELETE"]);

export function PluginUiPage() {
  const { id = "" } = useParams();
  const { t, i18n } = useTranslation();
  const frame = useRef<HTMLIFrameElement>(null);
  const [context, setContext] = useState<Record<string, unknown>>();
  const [error, setError] = useState<ApiError>();
  const [height, setHeight] = useState(720);

  useEffect(() => {
    let active = true;
    setError(undefined);
    void Promise.all([
      api.get<unknown>(`/plugins/${encodeURIComponent(id)}`),
      api.post<unknown>(`/plugins/${encodeURIComponent(id)}/ui-token`, {}),
    ])
      .then(([pluginResult, tokenResult]) => {
        if (!active) return;
        const plugin = objectFromPayload(pluginResult);
        const token = objectFromPayload(tokenResult);
        setContext({
          pluginId: id,
          name: stringValue(plugin["name"], id),
          entry: stringValue(token["entry"], "/"),
          token: stringValue(token["token"]),
        });
      })
      .catch((caught) => {
        if (active) setError(asApiError(caught));
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    const listener = (event: MessageEvent): void => {
      if (
        event.source !== frame.current?.contentWindow ||
        typeof event.data !== "object" ||
        event.data === null
      )
        return;
      const message = event.data as PluginRequestMessage;
      if (
        message.type === "aman:resize" &&
        typeof message.height === "number"
      ) {
        setHeight(Math.min(2_000, Math.max(320, message.height)));
        return;
      }
      if (
        message.type !== "aman:request" ||
        typeof message.requestId !== "string" ||
        typeof message.path !== "string" ||
        context === undefined
      )
        return;
      const path = message.path.replace(/^\/+/, "");
      const method =
        typeof message.method === "string"
          ? message.method.toUpperCase()
          : "GET";
      const target = event.source;
      if (
        !/^api(?:\/|$)/u.test(path) ||
        path.split("/").includes("..") ||
        path.includes("\\") ||
        !allowedMethods.has(method)
      ) {
        target?.postMessage(
          {
            type: "aman:response",
            requestId: message.requestId,
            ok: false,
            error: {
              code: "PLUGIN_UI_REQUEST_DENIED",
              message: "Plugin UI request is not allowed",
            },
          },
          { targetOrigin: "*" },
        );
        return;
      }
      const token = stringValue(context["token"]);
      void api
        .request<unknown>(`/plugin-ui/${encodeURIComponent(id)}/${path}`, {
          method,
          headers: { Authorization: `Bearer ${token}` },
          ...(message.body !== undefined && method !== "GET"
            ? { body: message.body as Record<string, unknown> }
            : {}),
        })
        .then((data) => {
          target?.postMessage(
            {
              type: "aman:response",
              requestId: message.requestId,
              ok: true,
              data,
            },
            { targetOrigin: "*" },
          );
        })
        .catch((caught) => {
          const requestError = asApiError(caught);
          target?.postMessage(
            {
              type: "aman:response",
              requestId: message.requestId,
              ok: false,
              error: {
                code: requestError.code,
                message: requestError.message,
                status: requestError.status,
              },
            },
            { targetOrigin: "*" },
          );
        });
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [context, id]);

  const initialize = (): void => {
    if (context === undefined) return;
    frame.current?.contentWindow?.postMessage(
      {
        type: "aman:init",
        token: context["token"],
        pluginId: id,
        locale: i18n.language,
        theme: document.documentElement.dataset["theme"] ?? "light",
      },
      "*",
    );
  };

  return (
    <div className="page-stack">
      <PageHeader
        title={`${t("pages.pluginUi.title")} — ${stringValue(context?.["name"], id)}`}
        subtitle={t("pages.pluginUi.secure")}
      />
      {error !== undefined ? (
        <Alert tone="error" title={t("pages.pluginUi.loadFailed")}>
          <code>{error.code}</code>
        </Alert>
      ) : null}
      <Card bodyClassName="plugin-frame-card">
        {context === undefined && error === undefined ? (
          <span className="aman-spinner" />
        ) : context !== undefined ? (
          <iframe
            ref={frame}
            title={stringValue(context["name"], id)}
            src={`/api/v1/plugin-ui/${encodeURIComponent(id)}${stringValue(context["entry"], "/")}`}
            sandbox="allow-scripts allow-forms allow-popups allow-modals"
            onLoad={initialize}
            style={{ height }}
          />
        ) : null}
      </Card>
    </div>
  );
}
